// ============================================================
// CaseBillingSection.tsx — Invoicing Stage 1: the case's BILLING section
// (Case file group; approved mockup screen 1). "Ready to bill £X" with the
// one-click Create bill (+ B shortcut), WIP / disbursement / billed / on-account
// tiles, the case's bills and its disbursements. Opening a bill swaps the
// section to that bill (BillView: builder or issued view).
// All £ are the server's (GET /api/cases/:id/billing). Members without billing
// access (lib/billingAccess.ts) see the disbursements card only.
// B3b — the case's credit notes are rows of their own (minus amounts), each
// under the bill it credits; opening one shows it in the main panel
// (BillView → CreditNoteView).
// ============================================================
import { useLivePermissions } from '../../hooks/useLivePermissions'
import { ErrorNote } from '../shared/ActionBox'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { goBack, initialPlaceParam, navigateTo, navigateWithin, usePlaceParam } from '../../navigation/navigate'
import { useBillingLive } from '../../hooks/useBillingLive'
import { useCaseChange } from '../../utils/caseChangeEvents'
import { getCaseBillingApi, listDisbursementsApi, updateDisbursementApi, deleteDisbursementApi } from '../../api/casevoApi'
import type { Bill, CaseBilling, CaseCreditNoteRow, Disbursement } from '../../types/billing.types'
import { categoryLabel } from '../../types/billing.types'
import { SectionHead } from '../case/sections/sectionKit'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import RaiseBillModal from './RaiseBillModal'
import DisbursementModal from './DisbursementModal'
import BillView from './BillView'
import { creditNotesByBill, creditRowAmount } from './CreditNoteView'
import WithheldAmount from '../shared/WithheldAmount'
import {
  BIcon, B_ICON, B_PRIMARY_LG, B_SECONDARY, B_SECONDARY_SM, BILL_TYPE_SHORT, CARD, CardHead, DisbStatusPill, EYEBROW, KpiSkeleton, KpiTile, NOTE, RowsSkeleton, Skel,
  PARTIAL_NOTE, StatusPill, settledLabel, TD, TH, TextPill, VatChip, YoursOnly, errorText, fmtDate, fmtDuration, money, moneyOrHidden, partialMoney, useCanUseBilling,
} from './billingUi'

interface Props { caseId: string; caseName: string; caseRef: string }

const CaseBillingSection: React.FC<Props> = ({ caseId, caseName, caseRef }) => {
  const canBill = useCanUseBilling()
  const [data, setData] = useState<CaseBilling | null>(null)
  const [disbOnly, setDisbOnly] = useState<Disbursement[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The open bill (+ the payload already in hand after "Create draft bill").
  // Part of the case's place (?caseId=…&section=billing&billId=… — the same
  // shape as the approval-email link): opening one is a history step, so Back
  // returns to the Billing list; a refresh / Back-Forward re-opens it.
  const [openBill, setOpenBill] = useState<{ id: string; initial?: Bill; creditNoteId?: string } | null>(() => {
    const id = initialPlaceParam('billId')
    return id ? { id } : null
  })
  usePlaceParam('billId', openBill?.id ?? null, (id) => {
    setOpenBill(id ? { id } : null)
    if (!id && dirty.current) void load()
  })
  const showBill = (b: { id: string; initial?: Bill; creditNoteId?: string }): void => { void navigateWithin(() => setOpenBill(b)) }
  // A bell row / email link asked for one of THIS case's bills → open it.
  const pendingBill = useCasevoStore((s) => s.pendingBill)
  useEffect(() => {
    if (!pendingBill || pendingBill.caseId !== caseId) return
    setOpenBill({ id: pendingBill.billId, initial: pendingBill.initial })
    useCasevoStore.getState().setPendingBill(null)
  }, [pendingBill, caseId])
  // PERF: edits inside an open bill no longer re-fetch this section on every
  // change (it's hidden meanwhile). Rows are patched from the server's bill,
  // and ONE reconcile runs on the way back (WIP / on-account figures can move).
  const dirty = useRef(false)
  const [raising, setRaising] = useState(false)
  // The "Invoice" action (case Activity tile / client-tab case picker) → open Create bill as
  // soon as this case's billing data is in (RaiseBillModal needs it).
  const pendingRaise = useCasevoStore((s) => s.pendingRaiseBillCaseId)
  useEffect(() => {
    if (pendingRaise !== caseId || !data) return
    useCasevoStore.getState().setPendingRaiseBillCaseId(null)
    setOpenBill(null)
    setRaising(true)
  }, [pendingRaise, caseId, data])
  const [editDisb, setEditDisb] = useState<Disbursement | null | 'new'>(null)
  const [delDisb, setDelDisb] = useState<Disbursement | null>(null)

  // `silent` = a live refresh: a failed background read keeps what's on screen.
  const load = useCallback(async (silent = false) => {
    try {
      if (canBill) setData(await getCaseBillingApi(caseId))
      else setDisbOnly(await listDisbursementsApi(caseId))
      setError(null); dirty.current = false
    } catch (e) { if (!silent) setError(errorText(e, 'Couldn’t load billing.')) }
  }, [caseId, canBill])
  useEffect(() => { void load() }, [load])

  // LIVE: a bill on THIS case changed (created / issued / paid / credited /
  // written off / edited / deleted) for a user allowed to see it, or the
  // socket reconnected / permissions changed → re-read the case's billing the
  // normal way. While a bill is open the list is hidden: mark it stale and
  // reconcile on the way back (as for local edits).
  const openBillRef = useRef(openBill); openBillRef.current = openBill
  useBillingLive((events, resync) => {
    if (!resync && !events.some((e) => e.caseId === caseId)) return
    if (openBillRef.current) { dirty.current = true; return }
    void load(true)
  }, { enabled: canBill })
  // (D12 live updates) Time, disbursements, payments… changed by someone else
  // (also for users who can't bill: their disbursements list).
  useCaseChange(caseId, ['bill', 'activity'], (_k, info) => {
    if (!info.remote) return
    if (openBillRef.current) { dirty.current = true; return }
    void load(true)
  })

  // ★ Live permission change: a lost money right clears the case's billing
  // (and closes a raise-bill draft built from it) AT ONCE, then re-reads.
  useLivePermissions((narrowed) => {
    if (narrowed) { setData(null); setRaising(false) }
    void load(true)
  })

  // "B" = Create bill (when nothing else has focus).
  useEffect(() => {
    if (!canBill || openBill || raising) return
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null
      if (e.key.toLowerCase() !== 'b' || e.metaKey || e.ctrlKey || e.altKey) return
      if (el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return
      if (data && (data.ready.entryCount > 0 || data.ready.disbursementCount > 0)) { e.preventDefault(); setRaising(true) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [canBill, openBill, raising, data])

  const disbs = data?.disbursements ?? disbOnly ?? []

  const patchBill = (b: Bill) => {
    dirty.current = true
    setData((d) => d && { ...d, bills: d.bills.map((r) => (r.id === b.id ? { ...r, number: b.number, status: b.status, billType: b.billType, submittedAt: b.submittedAt, issuedAt: b.issuedAt, dueAt: b.dueAt, totalPence: b.totals.totalPence, balancePence: b.balancePence, creditedPence: b.creditedPence, writtenOffPence: b.writeOff ? b.writeOff.amountPence : 0, amountPartial: !!b.totalsPartial } : r)) })
  }
  const dropBill = (id: string) => { dirty.current = true; setData((d) => d && { ...d, bills: d.bills.filter((r) => r.id !== id) }) }
  if (openBill) {
    return <BillView billId={openBill.id} initialBill={openBill.initial} initialCreditNoteId={openBill.creditNoteId ?? null} backLabel="Billing" onBack={() => goBack(() => { setOpenBill(null); if (dirty.current) void load() })} onChanged={patchBill} onDeleted={dropBill} />
  }

  const markPaid = async (d: Disbursement) => {
    try { await updateDisbursementApi(d.id, { status: 'PAID' }); void load() } catch (e) { setError(errorText(e, 'Couldn’t update the disbursement.')) }
  }

  const disbCard = (
    <div className={CARD}>
      <CardHead icon="card" title="Disbursements" sub="Paid disbursements are ready to bill; anticipated ones are held back until paid"
        right={<button type="button" className={B_SECONDARY_SM} onClick={() => setEditDisb('new')}><BIcon name="plus" size={13} />Add disbursement</button>} />
      {/* Still loading → a placeholder, never the "none recorded" empty line. */}
      {!data && disbOnly === null && !error ? <div className="px-5 pb-5" aria-busy="true"><Skel className="h-[13px] w-56" /></div>
        : disbs.length === 0 ? <p className={`${NOTE} px-5 pb-5 m-0`}>No disbursements recorded yet.</p> : (
        <div className="px-[10px] pb-2 overflow-x-auto">
          <table className="w-full border-collapse text-[13.5px] min-w-[620px]">
            <thead><tr><th className={TH}>Type</th><th className={TH}>Description</th><th className={TH}>VAT</th><th className={`${TH} text-right`}>Net</th><th className={`${TH} text-right`}>Status</th><th className={TH} style={{ width: 64 }} /></tr></thead>
            <tbody>
              {disbs.map((d) => (
                <tr key={d.id} className={d.status === 'ANTICIPATED' ? 'opacity-75' : ''}>
                  <td className={`${TD} text-[12.5px] text-casevo-text-soft w-[92px]`}>{categoryLabel(d.category)}</td>
                  <td className={TD}><div className="font-medium text-casevo-forest">{d.description}</div>
                    <div className={NOTE}>{[d.payableTo, d.status === 'ANTICIPATED' ? 'not yet paid' : d.status === 'BILLED' ? `billed on ${d.invoice?.number ?? 'a bill'}` : d.paidAt ? `paid ${fmtDate(d.paidAt)}` : ''].filter(Boolean).join(' · ')}</div></td>
                  <td className={TD}><VatChip t={d.vatTreatment} /></td>
                  <td className={`${TD} text-right tabular-nums`}>{money(d.amountPence)}</td>
                  <td className={`${TD} text-right`}><DisbStatusPill status={d.status} onDraft={!!d.onDraftInvoiceId} /></td>
                  <td className={`${TD} text-right whitespace-nowrap`}>
                    {d.status !== 'BILLED' && <>
                      {d.status === 'ANTICIPATED' && <button type="button" className={B_ICON} title="Mark paid" aria-label={`Mark ${d.description} paid`} onClick={() => { void markPaid(d) }}><BIcon name="check" size={13} /></button>}
                      <button type="button" className={B_ICON} title="Edit" aria-label={`Edit ${d.description}`} onClick={() => setEditDisb(d)}><BIcon name="pen" size={13} /></button>
                      {!d.onDraftInvoiceId && <button type="button" className={B_ICON} title="Delete" aria-label={`Delete ${d.description}`} onClick={() => setDelDisb(d)}><BIcon name="trash" size={13} /></button>}
                    </>}
                    {d.status === 'BILLED' && <span className="inline-grid place-items-center w-[30px] h-[30px] text-casevo-text-soft" title="Billed disbursements are locked"><BIcon name="lock" size={13} /></span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )

  const modals = <>
    {editDisb && <DisbursementModal caseId={caseId} existing={editDisb === 'new' ? null : editDisb} onClose={() => setEditDisb(null)} onSaved={() => { setEditDisb(null); void load() }} />}
    <ConfirmDialogue isOpen={!!delDisb} title="Delete this disbursement?" message={delDisb ? `“${delDisb.description}” (${money(delDisb.amountPence)}) will be removed from the case.` : ''} confirmLabel="Delete"
      onCancel={() => setDelDisb(null)} onConfirm={() => { const d = delDisb; setDelDisb(null); if (d) void deleteDisbursementApi(d.id).then(() => load()).catch((e) => setError(errorText(e, 'Couldn’t delete.'))) }} />
  </>

  const head = (
    <SectionHead title="Billing" actions={canBill ? <button type="button" className={B_SECONDARY} onClick={() => { void navigateTo({ view: 'billing' }) }}><BIcon name="bill" size={13} />All firm bills</button> : undefined} />
  )
  const errorBanner = error && <ErrorNote className="mb-3">{error}</ErrorNote>

  if (!canBill) {
    return <div className="flex flex-col gap-[14px]">{head}{errorBanner}
      <div className={`${CARD} px-5 py-4 ${NOTE} flex gap-2 items-center`}><BIcon name="lock" size={13} />Bills show every fee earner’s charges, so they’re limited to admins and members with “See colleagues’ billing”. You can still record disbursements here.</div>
      {disbCard}{modals}</div>
  }
  if (!data) return (
    <div className="flex flex-col gap-[14px]">{head}{errorBanner}
      <div data-skeleton className={`${CARD} px-[22px] py-5 flex items-center gap-5`}><div className="flex-1"><Skel className="h-[10px] w-24" /><Skel className="h-[26px] w-48 mt-2" /><Skel className="h-[11px] w-72 max-w-full mt-2" /></div><Skel className="h-[42px] w-36 rounded-[11px]" /></div>
      <KpiSkeleton /><RowsSkeleton rows={3} /><span className="sr-only" role="status">Loading billing…</span>
    </div>
  )

  const r = data.ready
  const wipUnits = data.wip.reduce((s, w) => s + w.units, 0)
  // Withheld colleague entries (null £) → the £ figures are the viewer's own, labelled "(yours)".
  const wipPence = data.wip.reduce((s, w) => s + (w.amountPence ?? 0), 0)
  const wipPartial = data.wip.some((w) => w.amountPence == null)
  const wipSecs = data.wip.reduce((s, w) => s + w.durationSeconds, 0)
  const anticipated = data.disbursements.filter((d) => d.status === 'ANTICIPATED')
  const paidReady = data.disbursements.filter((d) => d.status === 'PAID' && !d.invoiceId)
  const billedShare = data.billedToDatePence + wipPence > 0 ? Math.round((data.billedToDatePence / (data.billedToDatePence + wipPence)) * 100) : 0
  const hasReady = r.entryCount > 0 || r.disbursementCount > 0
  // B3b — each bill's credit notes, oldest first, listed under it.
  const creditsByBill = creditNotesByBill(data.creditNotes ?? [])
  const creditRow = (cn: CaseCreditNoteRow) => (
    <tr key={`cn-${cn.id}`} className="cursor-pointer hover:bg-casevo-input" onClick={() => showBill({ id: cn.invoiceId, creditNoteId: cn.id })}>
      <td className={`${TD} pl-[22px]`}>
        <button type="button" className="text-left focus:outline-none casevo-focus rounded-[3px]" onClick={(e) => { e.stopPropagation(); showBill({ id: cn.invoiceId, creditNoteId: cn.id }) }}
          aria-label={`Open credit note ${cn.number ?? '(draft)'} against ${cn.billNumber ?? 'its bill'}`}>
          {cn.number ? <span className="font-mono font-semibold text-casevo-forest">{cn.number}</span> : <span className="italic text-casevo-text-soft">Draft credit note</span>}
          <div className={NOTE}>Credits {cn.billNumber ?? 'a bill'}</div>
        </button>
      </td>
      <td className={TD}><TextPill>Credit note</TextPill></td>
      <td className={`${TD} tabular-nums`}>{fmtDate(cn.issuedAt ?? cn.createdAt)}</td>
      <td className={`${TD} text-right tabular-nums`}>{creditRowAmount(cn)}</td>
      <td className={`${TD} text-right tabular-nums text-casevo-text-soft`}>—</td>
      <td className={`${TD} text-right`}><TextPill>{cn.status === 'ISSUED' ? (cn.delivered ? 'Issued · sent' : 'Issued') : 'Draft'}</TextPill></td>
    </tr>
  )

  return (
    <div className="flex flex-col gap-[14px]">
      {head}{errorBanner}
      <div className={`${CARD} overflow-hidden`}>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-center px-[22px] py-5 bg-[linear-gradient(90deg,var(--casevo-mint),transparent_70%)]">
          <div>
            <div className={`${EYEBROW} !text-casevo-brand dark:!text-[#8fd6b0]`}>Ready to bill</div>
            <div className="text-[26px] font-semibold tracking-[-0.015em] mt-1 tabular-nums text-casevo-forest">{/* Restricted viewer + colleagues' time: never a bare £0.00 (reads as "nothing to bill") — lock or "partial" note. */}
              {r.amountPartial && r.totalPence === 0
                ? <><WithheldAmount size={20} /> <span className="text-[14px] font-medium text-casevo-text-soft">{PARTIAL_NOTE} — colleagues’ time is ready to bill</span></>
                : <>{money(r.totalPence)} <span className="text-[14px] font-medium text-casevo-text-soft">incl. VAT</span>{r.amountPartial && <YoursOnly />}</>}</div>
            <div className={`${NOTE} mt-[3px] text-[13px]`}>
              {hasReady
                ? <>{r.units} units of time ({partialMoney(r.profitCostsPence, r.amountPartial)}) · {r.disbursementCount} paid disbursement{r.disbursementCount === 1 ? '' : 's'} ({money(r.disbursementsPence)}){r.onAccountCreditPence != null && r.onAccountCreditPence > 0 && <> · less {money(r.onAccountCreditPence)} already paid on account</>}</>
                : 'Nothing unbilled on this case right now.'}
            </div>
          </div>
          <div className="flex flex-col items-start md:items-end gap-[7px]">
            <button type="button" className={B_PRIMARY_LG} onClick={() => setRaising(true)}><BIcon name="zap" size={14} />{hasReady ? 'Create bill' : 'New bill'}</button>
            <span className="text-[11.5px] text-casevo-text-soft">Everything unbilled is pre-selected · <kbd className="text-[11px] font-semibold px-[6px] py-px rounded-[5px] bg-casevo-input border border-casevo-border text-casevo-text-soft">B</kbd></span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Unbilled WIP" value={<>{partialMoney(wipPence, wipPartial)}</>} sub={`${wipUnits} units · ${fmtDuration(wipSecs)} · ${data.wip.length} ${data.wip.length === 1 ? 'entry' : 'entries'}`}>
          {!wipPartial && <div className="h-[6px] rounded bg-casevo-border-soft mt-[10px] flex overflow-hidden" title={`${billedShare}% of this case’s time is billed`}>
            <i className="block h-full bg-casevo-brand" style={{ width: `${billedShare}%` }} /><i className="block h-full bg-casevo-light" style={{ width: `${100 - billedShare}%` }} />
          </div>}
        </KpiTile>
        <KpiTile label="Disbursements" value={money(paidReady.reduce((s, d) => s + d.amountPence, 0))} sub={`${paidReady.length} paid${anticipated.length ? ` · ${money(anticipated.reduce((s, d) => s + d.amountPence, 0))} anticipated` : ''}`} />
        <KpiTile label="Billed to date" value={<>{partialMoney(data.billedToDatePence, data.billedToDatePartial)}</>} sub={`${data.billedCount} bill${data.billedCount === 1 ? '' : 's'}`} />
        {/* null → hidden: a bill with colleagues' billing drew on this money, so what's left would give its total away. */}
        <KpiTile label="Held on account" value={moneyOrHidden(data.onAccountAvailablePence)}
          sub={data.onAccountAvailablePence == null ? 'Hidden — a bill with colleagues’ billing drew on it' : data.onAccountAvailablePence > 0 ? 'Credited on the next bill' : 'Nothing held'} />
      </div>

      <div className={CARD}>
        <CardHead icon="bill" title="Bills on this case" sub="Drafts, issued bills, credit notes and payments for this matter" />
        {data.bills.length === 0 ? <p className={`${NOTE} px-5 pb-5 m-0`}>No bills yet.</p> : (
          <div className="px-[10px] pb-2 overflow-x-auto">
            <table className="w-full border-collapse text-[13.5px] min-w-[600px]">
              <thead><tr><th className={TH}>Bill</th><th className={TH}>Type</th><th className={TH}>Date</th><th className={`${TH} text-right`}>Total</th><th className={`${TH} text-right`}>Balance</th><th className={`${TH} text-right`}>Status</th></tr></thead>
              <tbody>
                {data.bills.map((b) => {
                  const overdue = (b.status === 'ISSUED' || b.status === 'PART_PAID') && !!b.dueAt && new Date(b.dueAt).getTime() < Date.now()
                  return (
                    <React.Fragment key={b.id}>
                    <tr className="cursor-pointer hover:bg-casevo-input" onClick={() => showBill({ id: b.id })}>
                      <td className={TD}>
                        <button type="button" className="text-left focus:outline-none casevo-focus rounded-[3px]" onClick={(e) => { e.stopPropagation(); showBill({ id: b.id }) }}>
                          {b.number ? <span className="font-mono font-semibold text-casevo-forest">{b.number}</span> : <span className="italic text-casevo-text-soft">Draft · not numbered</span>}
                          <div className={NOTE}>Raised by {b.createdBy ?? '—'}</div>
                        </button>
                      </td>
                      <td className={TD}><TextPill>{BILL_TYPE_SHORT[b.billType]}</TextPill></td>
                      <td className={`${TD} tabular-nums`}>{fmtDate(b.issuedAt ?? b.createdAt)}</td>
                      <td className={`${TD} text-right tabular-nums`}>{partialMoney(b.totalPence, b.amountPartial)}</td>
                      <td className={`${TD} text-right tabular-nums ${b.balancePence === 0 || b.status === 'VOID' ? 'text-casevo-text-soft' : 'font-semibold'}`}>{b.status === 'VOID' ? '—' : moneyOrHidden(b.balancePence)}</td>
                      <td className={`${TD} text-right`}><StatusPill status={b.status} overdue={overdue} awaiting={!!b.submittedAt} settled={settledLabel(b)} /></td>
                    </tr>
                    {(creditsByBill.get(b.id) ?? []).map(creditRow)}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {disbCard}
      {raising && <RaiseBillModal caseId={caseId} caseName={data.case?.name ?? caseName} caseRef={data.case?.ref ?? caseRef} data={data} onClose={() => setRaising(false)} onCreated={(b) => { setRaising(false); dirty.current = true; showBill({ id: b.id, initial: b }) }} />}
      {modals}
    </div>
  )
}

export default CaseBillingSection
